// src/hooks/useBrandListings.ts
import { useCallback, useEffect, useState } from "react";
import { getAllBrandListings } from "../services/Brandlistings.api";
import { BrandListing, BrandListingFilters } from "../types/brandlistingsmodel";

interface UseBrandListingsReturn {
  listings: BrandListing[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches listings from GET /api/brand/get-approved-listing with the given
 * server-side filters (State/City/MediaType/MinRent/MaxRent). Since the
 * endpoint has no page/pageSize params, pagination is applied client-side
 * on top of the returned array (see PAGE_SIZE in utils/listingFormat.ts).
 */
export function useBrandListings(filters: BrandListingFilters): UseBrandListingsReturn {
  const [listings, setListings] = useState<BrandListing[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getAllBrandListings(filters)
      .then((res) => {
        if (cancelled) return;
        setListings(res.listings ?? []);
        setTotal(res.total ?? 0);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || "Failed to load listings. Please try again.");
        setListings([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
 
  }, [filters.state, filters.city, filters.mediaType, filters.minRent, filters.maxRent, reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  return { listings, total, isLoading, error, refetch };
}